import { useState, useEffect, useMemo } from "react";
import { useRoute } from "wouter";
import { Calendar, Clock, User, Mail, Phone, MessageSquare, CheckCircle, ArrowRight, ArrowLeft, Loader2, Zap, ExternalLink } from "lucide-react";

interface SlotData {
  start: string;
  end: string;
  label?: string;
}

interface SlotsResponse {
  slots: SlotData[];
  businessName: string;
  meetingTitle: string;
  meetingDescription: string;
  durationMins: number;
  timezone: string;
  horizonDays?: number;
}

interface BookingResponse {
  success: boolean;
  appointment: {
    id: number;
    title: string;
    startAt: string;
    endAt: string;
    meetingLink?: string;
    status: string;
    label?: string;
  };
  message: string;
  /** true only when the confirmation email actually went out */
  confirmationSent?: boolean;
  manageUrl?: string | null;
}

// Dates are calendar days (YYYY-MM-DD) and times are shown in the BUSINESS's time zone (the slots API works in that zone), labelled with it.
function generateDates(count: number): Date[] {
  const dates: Date[] = [];
  const today = new Date();
  for (let i = 0; i < count; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    dates.push(d);
  }
  return dates;
}

/** The local calendar date (not the UTC one: in the evening toISOString() is already tomorrow). */
function formatDate(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function formatDayName(date: Date): string {
  return date.toLocaleDateString("en-US", { weekday: "short" });
}

function formatDayNumber(date: Date): string {
  return date.getDate().toString();
}

function formatMonthYear(date: Date): string {
  return date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
}

function formatSlotTime(iso: string, tz?: string): string {
  const d = new Date(iso);
  try { return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true, ...(tz ? { timeZone: tz } : {}) }); }
  catch { return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true }); }
}

function formatFullDate(iso: string, tz?: string): string {
  const d = new Date(iso);
  try { return d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric", ...(tz ? { timeZone: tz } : {}) }); }
  catch { return d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" }); }
}

function zoneName(iso: string, tz?: string): string {
  if (!tz) return "";
  try {
    const p = new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "short" }).formatToParts(new Date(iso));
    return p.find((x) => x.type === "timeZoneName")?.value || tz;
  } catch { return tz; }
}

export default function PublicBooking() {
  const [, params] = useRoute("/book/:slug");
  const wsId = params?.slug;

  const dates = useMemo(() => generateDates(14), []);
  // a time clicked in an email (?start=<ISO>) opens that day with that time chosen; otherwise the page opens on the first day that
  // has open times (Bader, 2026-10-08: in the evening "today" showed "No available times" and nothing else)
  const wantedStart = useMemo(() => {
    try { const s = new URLSearchParams(window.location.search).get("start"); return s && !isNaN(Date.parse(s)) ? s : null; } catch { return null; }
  }, []);
  const initialDate = useMemo(() => {
    if (wantedStart) { const d = formatDate(new Date(wantedStart)); if (dates.some((x) => formatDate(x) === d)) return d; }
    return formatDate(dates[0]);
  }, [dates, wantedStart]);
  const [selectedDate, setSelectedDate] = useState<string>(initialDate);
  const [autoAdvance, setAutoAdvance] = useState(!wantedStart);
  const [slots, setSlots] = useState<SlotData[]>([]);
  const [meta, setMeta] = useState<Omit<SlotsResponse, "slots"> | null>(null);
  const [loadingSlots, setLoadingSlots] = useState(true);
  const [slotsError, setSlotsError] = useState("");
  // bumped by "Try again" (and after a time was taken) so the slots load again for the same day
  const [reloadKey, setReloadKey] = useState(0);

  const [selectedSlot, setSelectedSlot] = useState<SlotData | null>(null);
  const [formName, setFormName] = useState("");
  const [formEmail, setFormEmail] = useState("");
  const [formPhone, setFormPhone] = useState("");
  const [formNotes, setFormNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const [booking, setBooking] = useState<BookingResponse | null>(null);

  // "pick a time now" after a website form (?lt=<signed token>): their name, email and phone are filled in, never typed twice
  useEffect(() => {
    let lt = "";
    try { lt = new URLSearchParams(window.location.search).get("lt") || ""; } catch { /* no query */ }
    if (!wsId || !lt) return;
    fetch(`/api/public/booking/${wsId}/prefill?lt=${encodeURIComponent(lt)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((p) => {
        if (!p) return;
        setFormName((v) => v || String(p.name || ""));
        setFormEmail((v) => v || String(p.email || ""));
        setFormPhone((v) => v || String(p.phone || ""));
      })
      .catch(() => {});
  }, [wsId]);

  useEffect(() => {
    if (!wsId || !selectedDate) return;
    setLoadingSlots(true);
    setSlotsError("");
    setSlots([]);
    fetch(`/api/public/booking/${wsId}/slots?date=${selectedDate}`)
      .then(async (r) => {
        if (!r.ok) {
          const body = await r.json().catch(() => null);
          throw new Error(r.status === 404 ? "Online booking isn't available right now." : body?.message || "Unable to load available times");
        }
        return r.json();
      })
      .then((data: SlotsResponse) => {
        const list = data.slots || [];
        // nothing open on the day the page opened on: move to the next day (until the visitor picks a day themselves)
        if (autoAdvance && list.length === 0) {
          const i = dates.findIndex((d) => formatDate(d) === selectedDate);
          if (i >= 0 && i < dates.length - 1) { setSelectedDate(formatDate(dates[i + 1])); return; }
          setAutoAdvance(false);
        }
        if (autoAdvance) setAutoAdvance(false);
        setSlots(list);
        if (wantedStart) {
          const hit = list.find((s) => Date.parse(s.start) === Date.parse(wantedStart));
          if (hit) setSelectedSlot(hit);
        }
        setMeta({
          businessName: data.businessName,
          meetingTitle: data.meetingTitle,
          meetingDescription: data.meetingDescription,
          durationMins: data.durationMins,
          timezone: data.timezone,
          horizonDays: data.horizonDays,
        });
      })
      .catch((e) => setSlotsError(e.message))
      .finally(() => setLoadingSlots(false));
  }, [wsId, selectedDate, reloadKey]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedSlot || !wsId) return;
    if (!formName.trim() || !formEmail.trim()) {
      setSubmitError("Name and email are required.");
      return;
    }
    setSubmitting(true);
    setSubmitError("");
    try {
      const res = await fetch(`/api/public/booking/${wsId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          slotStart: selectedSlot.start,
          slotEnd: selectedSlot.end,
          name: formName.trim(),
          email: formEmail.trim(),
          phone: formPhone.trim() || undefined,
          notes: formNotes.trim() || undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 409) {
        // the time was taken (or is no longer open): show fresh times for that day
        setSubmitError(data.message || "That time is no longer open. Please pick another.");
        setSelectedSlot(null);
        setReloadKey((k) => k + 1);
        return;
      }
      if (!res.ok || !data.success) throw new Error(data.message || "Booking failed");
      setBooking(data);
    } catch (err: any) {
      setSubmitError(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (booking) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center px-4" data-testid="booking-success">
        <div className="max-w-md w-full text-center space-y-6">
          <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto">
            <CheckCircle size={32} className="text-emerald-400" />
          </div>
          <div className="space-y-2">
            <h2 className="text-2xl font-bold" data-testid="text-booking-confirmed">You're booked</h2>
            <p className="text-sm text-foreground/60" data-testid="text-booking-message">{booking.message || "Your appointment has been scheduled."}</p>
          </div>
          <div className="bg-foreground/[0.03] border border-foreground/[0.08] rounded-2xl p-6 text-left space-y-4">
            <div className="flex items-start gap-3">
              <Calendar size={16} className="text-blue-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs text-foreground/30 uppercase tracking-wider font-bold mb-1">Date & Time</p>
                <p className="text-sm text-foreground" data-testid="text-booking-datetime">
                  {formatFullDate(booking.appointment.startAt, meta?.timezone)}
                </p>
                <p className="text-sm text-foreground/60">
                  {formatSlotTime(booking.appointment.startAt, meta?.timezone)} - {formatSlotTime(booking.appointment.endAt, meta?.timezone)} {zoneName(booking.appointment.startAt, meta?.timezone)}
                </p>
              </div>
            </div>
            {booking.appointment.title && (
              <div className="flex items-start gap-3">
                <MessageSquare size={16} className="text-blue-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-foreground/30 uppercase tracking-wider font-bold mb-1">Meeting</p>
                  <p className="text-sm text-foreground" data-testid="text-booking-title">{booking.appointment.title}</p>
                </div>
              </div>
            )}
            {booking.appointment.meetingLink && (
              <div className="flex items-start gap-3">
                <ExternalLink size={16} className="text-blue-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-foreground/30 uppercase tracking-wider font-bold mb-1">Meeting Link</p>
                  <a
                    href={booking.appointment.meetingLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-400 hover:text-blue-300 underline break-all"
                    data-testid="link-meeting"
                  >
                    {booking.appointment.meetingLink}
                  </a>
                </div>
              </div>
            )}
          </div>
          {booking.manageUrl && (
            <a href={booking.manageUrl} className="inline-block text-sm text-blue-400 hover:text-blue-300 underline" data-testid="link-manage-booking">
              Reschedule or cancel
            </a>
          )}
          {booking.confirmationSent ? (
            <p className="text-xs text-foreground/40" data-testid="text-confirmation-status">A confirmation with a calendar invite was emailed to you.</p>
          ) : (
            <p className="text-xs text-foreground/40" data-testid="text-confirmation-status">We couldn't email a confirmation, so please note the time{booking.manageUrl ? " and keep the link above" : ""}.</p>
          )}
        </div>
      </div>
    );
  }

  if (selectedSlot) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center px-4" data-testid="booking-form-view">
        <div className="max-w-md w-full space-y-6">
          <button
            onClick={() => { setSelectedSlot(null); setSubmitError(""); }}
            className="flex items-center gap-2 text-sm text-foreground/40 hover:text-foreground/70 transition-colors"
            data-testid="button-back-to-slots"
          >
            <ArrowLeft size={16} />
            <span>Back to time slots</span>
          </button>

          <div className="text-center space-y-2">
            {meta?.businessName && (
              <p className="text-xs text-blue-400 uppercase tracking-[0.15em] font-bold">{meta.businessName}</p>
            )}
            <h2 className="text-2xl font-bold" data-testid="text-form-heading">Book Your Appointment</h2>
          </div>

          <div className="bg-foreground/[0.03] border border-foreground/[0.08] rounded-xl p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-600/10 border border-blue-500/20 flex items-center justify-center shrink-0">
              <Clock size={18} className="text-blue-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-foreground" data-testid="text-selected-slot">
                {formatFullDate(selectedSlot.start, meta?.timezone)}
              </p>
              <p className="text-xs text-foreground/40">
                {formatSlotTime(selectedSlot.start, meta?.timezone)} - {formatSlotTime(selectedSlot.end, meta?.timezone)} {zoneName(selectedSlot.start, meta?.timezone)}
                {meta?.durationMins ? ` (${meta.durationMins} min)` : ""}
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="booking-form">
            <div>
              <label className="flex items-center gap-2 text-xs font-bold text-foreground/40 uppercase tracking-wider mb-2">
                <User size={12} />
                Name *
              </label>
              <input
                type="text"
                required
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="Your full name"
                className="w-full px-4 py-3 bg-foreground/[0.04] border border-foreground/[0.08] rounded-xl text-foreground placeholder-foreground/20 text-sm focus:outline-none focus:border-blue-500/40 focus:ring-1 focus:ring-blue-500/20 transition-all"
                data-testid="input-name"
              />
            </div>
            <div>
              <label className="flex items-center gap-2 text-xs font-bold text-foreground/40 uppercase tracking-wider mb-2">
                <Mail size={12} />
                Email *
              </label>
              <input
                type="email"
                required
                value={formEmail}
                onChange={(e) => setFormEmail(e.target.value)}
                placeholder="you@company.com"
                className="w-full px-4 py-3 bg-foreground/[0.04] border border-foreground/[0.08] rounded-xl text-foreground placeholder-foreground/20 text-sm focus:outline-none focus:border-blue-500/40 focus:ring-1 focus:ring-blue-500/20 transition-all"
                data-testid="input-email"
              />
            </div>
            <div>
              <label className="flex items-center gap-2 text-xs font-bold text-foreground/40 uppercase tracking-wider mb-2">
                <Phone size={12} />
                Phone
              </label>
              <input
                type="tel"
                value={formPhone}
                onChange={(e) => setFormPhone(e.target.value)}
                placeholder="+1 (555) 000-0000"
                className="w-full px-4 py-3 bg-foreground/[0.04] border border-foreground/[0.08] rounded-xl text-foreground placeholder-foreground/20 text-sm focus:outline-none focus:border-blue-500/40 focus:ring-1 focus:ring-blue-500/20 transition-all"
                data-testid="input-phone"
              />
            </div>
            <div>
              <label className="flex items-center gap-2 text-xs font-bold text-foreground/40 uppercase tracking-wider mb-2">
                <MessageSquare size={12} />
                Notes
              </label>
              <textarea
                value={formNotes}
                onChange={(e) => setFormNotes(e.target.value)}
                placeholder="Anything you'd like us to know..."
                rows={3}
                className="w-full px-4 py-3 bg-foreground/[0.04] border border-foreground/[0.08] rounded-xl text-foreground placeholder-foreground/20 text-sm focus:outline-none focus:border-blue-500/40 focus:ring-1 focus:ring-blue-500/20 transition-all resize-none"
                data-testid="input-notes"
              />
            </div>

            {submitError && (
              <div className="px-4 py-3 bg-red-500/10 border border-red-500/20 rounded-xl text-sm text-red-400" data-testid="text-submit-error">
                {submitError}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting || !formName.trim() || !formEmail.trim()}
              className="w-full inline-flex items-center justify-center gap-3 px-8 py-4 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-foreground font-bold rounded-xl text-sm transition-all"
              data-testid="button-confirm-booking"
            >
              {submitting ? <Loader2 size={18} className="animate-spin" /> : <ArrowRight size={18} />}
              <span>{submitting ? "Confirming..." : "Confirm Booking"}</span>
            </button>
          </form>
        </div>
      </div>
    );
  }

  const selectedDateObj = dates.find((d) => formatDate(d) === selectedDate) || dates[0];

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col" data-testid="booking-page">
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="max-w-lg w-full space-y-8">
          <div className="text-center space-y-3">
            <div className="w-12 h-12 rounded-xl bg-blue-600 flex items-center justify-center mx-auto mb-4">
              <Zap size={22} className="text-foreground" />
            </div>
            {meta?.businessName ? (
              <h1 className="text-2xl md:text-3xl font-bold" data-testid="text-business-name">{meta.businessName}</h1>
            ) : (
              <h1 className="text-2xl md:text-3xl font-bold">Book an Appointment</h1>
            )}
            {meta?.meetingTitle && (
              <p className="text-base text-foreground/60 font-medium" data-testid="text-meeting-title">{meta.meetingTitle}</p>
            )}
            {meta?.meetingDescription && (
              <p className="text-sm text-foreground/30" data-testid="text-meeting-description">{meta.meetingDescription}</p>
            )}
            {meta?.durationMins && (
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-foreground/[0.04] border border-foreground/[0.08] rounded-full text-xs text-foreground/40">
                <Clock size={12} />
                <span>{meta.durationMins} minutes</span>
                {meta.timezone && <span className="text-foreground/20">({meta.timezone})</span>}
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm text-foreground/40">
              <Calendar size={14} />
              <span className="font-medium">{formatMonthYear(selectedDateObj)}</span>
            </div>

            <div className="grid grid-cols-7 gap-2" data-testid="date-picker">
              {dates.map((date) => {
                const dateStr = formatDate(date);
                const isSelected = dateStr === selectedDate;
                const isToday = formatDate(new Date()) === dateStr;
                return (
                  <button
                    key={dateStr}
                    onClick={() => { setAutoAdvance(false); setSelectedDate(dateStr); setSelectedSlot(null); setSubmitError(""); }}
                    className={`flex flex-col items-center py-3 rounded-xl transition-all text-center ${
                      isSelected
                        ? "bg-blue-600 text-foreground"
                        : "bg-foreground/[0.03] border border-foreground/[0.06] text-foreground/50 hover:border-foreground/15 hover:text-foreground/80"
                    }`}
                    data-testid={`date-${dateStr}`}
                  >
                    <span className="text-[10px] uppercase tracking-wider font-medium opacity-70">{formatDayName(date)}</span>
                    <span className={`text-lg font-bold ${isSelected ? "text-foreground" : ""}`}>{formatDayNumber(date)}</span>
                    {isToday && !isSelected && (
                      <div className="w-1 h-1 rounded-full bg-blue-400 mt-0.5" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-3" data-testid="slots-container">
            <h3 className="text-sm font-bold text-foreground/40 uppercase tracking-wider flex items-center gap-2">
              <Clock size={14} />
              Available Times
            </h3>

            {loadingSlots ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 size={24} className="animate-spin text-blue-500" />
              </div>
            ) : slotsError ? (
              <div className="text-center py-8 space-y-2">
                <p className="text-sm text-red-400" data-testid="text-slots-error">{slotsError}</p>
                <button
                  onClick={() => setReloadKey((k) => k + 1)}
                  className="text-xs text-blue-400 hover:text-blue-300"
                  data-testid="button-retry-slots"
                >
                  Try again
                </button>
              </div>
            ) : slots.length === 0 ? (
              <div className="text-center py-8" data-testid="text-no-slots">
                <p className="text-sm text-foreground/30">No available times for this date.</p>
                <p className="text-xs text-foreground/15 mt-1">Try selecting a different day.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {submitError && (
                  <div className="px-4 py-3 bg-red-500/10 border border-red-500/20 rounded-xl text-sm text-red-400" data-testid="text-slot-taken">
                    {submitError}
                  </div>
                )}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {slots.map((slot, i) => (
                    <button
                      key={slot.start}
                      onClick={() => { setSelectedSlot(slot); setSubmitError(""); }}
                      className="px-4 py-3 bg-foreground/[0.03] border border-foreground/[0.08] rounded-xl text-sm font-medium text-foreground/70 hover:border-blue-500/40 hover:text-foreground hover:bg-blue-600/5 transition-all text-center"
                      data-testid={`slot-${i}`}
                    >
                      {formatSlotTime(slot.start, meta?.timezone)}
                    </button>
                  ))}
                </div>
                {meta?.timezone && slots[0] && (
                  <p className="text-[11px] text-foreground/30 text-center" data-testid="text-slots-timezone">Times are in {zoneName(slots[0].start, meta.timezone)} ({meta.timezone})</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <footer className="py-6 text-center">
        <div className="flex items-center justify-center gap-1.5 text-foreground/15 text-xs">
          <span>Powered by</span>
          <div className="flex items-baseline gap-0.5">
            <span className="font-bold text-foreground/25">Drpr</span>
            <span className="font-bold text-blue-500/40">.ai</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
